import React from "react";
import { Segmented } from "../ui";
import { useUI } from "@/lib/theme";
import { NotificationBell } from "../NotificationBell";

const RANGES: { key: string; days: number | null }[] = [
  { key: "range_all", days: null },
  { key: "range_12m", days: 365 },
  { key: "range_3m", days: 90 },
  { key: "range_30d", days: 30 },
  { key: "range_week", days: 7 },
];

export function ContentHeader({
  title,
  breadcrumb,
  showRange,
  rangeDays,
  onRangeChange,
  syncing,
  onSync,
  readOnly,
  demo,
}: {
  title: string;
  breadcrumb?: string;
  showRange: boolean;
  rangeDays: number | null;
  onRangeChange: (v: number | null) => void;
  syncing: boolean;
  onSync: () => void;
  readOnly: boolean;
  demo?: boolean;
}) {
  const { t } = useUI();

  return (
    <div className="sticky top-0 z-20 border-b border-line bg-panel/85 backdrop-blur">
      <div className="flex items-center justify-between gap-3 px-6 py-3">
        <div className="flex min-w-0 flex-col">
          {breadcrumb && (
            <span className="text-[11px] font-medium text-muted">{breadcrumb}</span>
          )}
          <h1 className="truncate text-[23px] font-extrabold tracking-tight text-fg leading-tight">
            {title}
          </h1>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {showRange && (
            <Segmented
              ariaLabel="range"
              variant="plain"
              value={String(rangeDays)}
              onChange={(v) => onRangeChange(v === "null" ? null : Number(v))}
              options={RANGES.map((r) => ({ value: String(r.days), label: t(r.key) }))}
            />
          )}
          <NotificationBell demo={demo} />
          {!readOnly && (
            <button
              onClick={onSync}
              disabled={syncing}
              className="flex items-center gap-1.5 rounded-xl border border-accent/40 bg-accent/10 px-3 py-1.5 text-sm font-semibold text-accent transition hover:bg-accent/20 disabled:cursor-default disabled:opacity-60"
            >
              {syncing ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-accent/30 border-t-accent" role="status" aria-label="Sincronizando" />
              ) : (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M23 4v6h-6M1 20v-6h6" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
              )}
              {syncing ? t("syncing") : t("sync")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function SyncNotice({ type }: { type: "rate_limited" | "timeout" }) {
  const { t } = useUI();
  return (
    <div className="mx-6 mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
      {t(type === "rate_limited" ? "sync_rate_limited" : "sync_timeout")}
    </div>
  );
}

export function SyncWarning({ lastSyncedAt }: { lastSyncedAt?: string }) {
  const { t } = useUI();
  if (!lastSyncedAt) return null;
  const d0 = new Date(lastSyncedAt + "T00:00:00");
  const d1 = new Date();
  const days = Math.round(
    (new Date(d1.getFullYear(), d1.getMonth(), d1.getDate()).getTime() -
      new Date(d0.getFullYear(), d0.getMonth(), d0.getDate()).getTime()) /
      86400000
  );
  if (days < 2) return null;
  return (
    <div className="mx-6 mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-600 dark:text-amber-400">
      <div className="flex items-start gap-2">
        <span aria-hidden>⚠️</span>
        <p>{t("warn_sync", { d: days })}</p>
      </div>
    </div>
  );
}
